import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import toast from 'react-hot-toast';
import { FaTimes, FaCopy, FaMapMarkerAlt, FaCrosshairs } from 'react-icons/fa';
import { adminApi } from '../../services/api';

const ClubModals = ({
    modalType,
    formData,
    setFormData,
    handleSubmit,
    closeModal,
    projects,
    attendanceList,
    clubType,
    projectCategories,
    loading
}) => {
    const [geoLoading, setGeoLoading] = useState(false);
    const [geoError, setGeoError] = useState('');
    const [mapLink, setMapLink] = useState('');
    const [linkLoading, setLinkLoading] = useState(false);

    const handleResolveLink = async () => {
        if (!mapLink) return;
        setLinkLoading(true);
        setGeoError('');
        const res = await adminApi.resolveMapLink(mapLink);
        setLinkLoading(false);
        if (res.success) {
            setFormData(prev => ({
                ...prev,
                latitude: parseFloat(res.data.latitude),
                longitude: parseFloat(res.data.longitude)
            }));
            toast.success('Coordinates extracted successfully!');
            setMapLink('');
        } else {
            setGeoError(res.message || 'Failed to extract coordinates from link');
        }
    };

    const fetchGpsLocation = () => {
        if (!navigator.geolocation) {
            setGeoError("Geolocation is not supported by your browser");
            return;
        }
        
        setGeoLoading(true);
        setGeoError('');
        
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setFormData(prev => ({
                    ...prev,
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                }));
                setGeoLoading(false);
            },
            (error) => {
                setGeoError("Unable to retrieve your location");
                setGeoLoading(false);
            }
        );
    };

    const inputClasses = "w-full px-5 py-3.5 rounded-xl border border-slate-100 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-sm font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-normal";
    const labelClasses = "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1";

    if (modalType === 'view-qr') {
        const qrValue = `${window.location.origin}/attendance/${formData.qr_code_token}`;
        return (
            <div className="flex flex-col items-center justify-center space-y-8 py-4">
                <div className="text-center space-y-2">
                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Project Access Point</h3>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Scan to register attendance for {formData.name}</p>
                </div>

                <div className="p-8 bg-white rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] border border-slate-50">
                    <QRCodeCanvas value={qrValue} size={220} level={"H"} includeMargin={false} />
                </div>

                <div className="w-full max-w-sm space-y-4">
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center gap-3">
                        <p className="text-[10px] font-bold text-slate-500 truncate flex-1">{qrValue}</p>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(qrValue);
                                toast.success('Link copied to dashboard!');
                            }}
                            className="w-8 h-8 rounded-xl bg-white border border-slate-100 text-indigo-600 flex items-center justify-center hover:bg-indigo-50 transition-colors shadow-sm"
                        >
                            <FaCopy size={12} />
                        </button>
                    </div>
                    <button onClick={closeModal} className="w-full py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all">
                        Dismiss
                    </button>
                </div>
            </div>
        );
    }

    if (modalType === 'view-attendance') {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-end border-b border-slate-50 pb-4">
                    <div>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-1">Live Statistics</p>
                        <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">{formData.name}</h3>
                    </div>
                </div>

                <div className="overflow-x-auto no-scrollbar rounded-2xl border border-slate-50 shadow-sm">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-50">
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Attendee</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Identity</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {attendanceList.length === 0 ? (
                                <tr><td colSpan="3" className="px-6 py-12 text-center text-slate-400 font-bold text-xs">No synchronization records found</td></tr>
                            ) : (
                                attendanceList.map((record) => (
                                    <tr key={record.id} className="bg-white hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-black text-xs uppercase">
                                                    {record.member_name.charAt(0)}
                                                </div>
                                                <span className="text-sm font-black text-slate-900">{record.member_name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-xs font-bold text-slate-500">{record.admission_number || record.nic_number || '-'}</td>
                                        <td className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                            {new Date(record.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <button onClick={closeModal} className="w-full py-4 bg-slate-50 text-slate-500 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-all border border-slate-100">
                    Close Monitor
                </button>
            </div>
        );
    }

    if (modalType === 'create-member' || modalType === 'edit-member') {
        const isAlpha = clubType === 'Alpha';
        const isOmega = clubType === 'Omega';

        return (
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className={labelClasses}>Full Identification Name</label>
                        <input
                            type="text"
                            value={formData.name || ''}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            placeholder="e.g. Leo John Doe"
                            className={inputClasses}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className={labelClasses}>System Access Email</label>
                        <input
                            type="email"
                            value={formData.email || ''}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            placeholder="leo@domain.com"
                            className={inputClasses}
                        />
                    </div>

                    {!formData.id && (
                        <div className="md:col-span-2 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/50 flex items-start gap-4">
                            <div className="w-6 h-6 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">!</div>
                            <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest leading-relaxed">
                                Security Protocol: Default password will be set to the user's Identification Number automatically.
                            </p>
                        </div>
                    )}

                    {(!clubType || isAlpha) && (
                        <div className={!clubType ? '' : 'md:col-span-2'}>
                            <label className={labelClasses}>Alpha Admission #</label>
                            <input
                                type="text"
                                value={formData.admission_number || ''}
                                onChange={(e) => setFormData({ ...formData, admission_number: e.target.value })}
                                placeholder="Identification Required"
                                required={isAlpha}
                                disabled={!!formData.id}
                                className={inputClasses}
                            />
                        </div>
                    )}
                    {(!clubType || isOmega) && (
                        <div className={!clubType ? '' : 'md:col-span-2'}>
                            <label className={labelClasses}>Omega NIC Number</label>
                            <input
                                type="text"
                                value={formData.nic_number || ''}
                                onChange={(e) => setFormData({ ...formData, nic_number: e.target.value })}
                                placeholder="Identification Required"
                                required={isOmega}
                                disabled={!!formData.id}
                                className={inputClasses}
                            />
                        </div>
                    )}

                    <div className="md:col-span-2">
                        <label className={labelClasses}>Organizational Position</label>
                        <select
                            value={formData.position || 'Member'}
                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                            required
                            className={inputClasses}
                        >
                            <option value="President">President</option>
                            <option value="Vice President">Vice President</option>
                            <option value="Secretary">Secretary</option>
                            <option value="Treasurer">Treasurer</option>
                            <option value="Executive Member">Executive Member</option>
                            <option value="Board Member">Board Member</option>
                            <option value="Member">Member</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-4 pt-6">
                    <button type="button" onClick={closeModal} className="flex-1 py-4 text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:text-slate-900 transition-colors">
                        Cancel
                    </button>
                    <button type="submit" disabled={loading} className="flex-[2] py-4 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                        {loading ? 'Processing...' : (formData.id ? 'Commit Changes' : 'Initialize Member')}
                    </button>
                </div>
            </form>
        );
    }

    if (modalType === 'mark-attendance') {
        return (
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className={labelClasses}>Target Project</label>
                    <select
                        value={formData.project_id || ''}
                        onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                        required
                        disabled={formData.fixedProject}
                        className={inputClasses}
                    >
                        <option value="">Select Target Deployment</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelClasses}>Attendee Full Name</label>
                    <input
                        type="text"
                        value={formData.member_name || ''}
                        onChange={(e) => setFormData({ ...formData, member_name: e.target.value })}
                        required
                        placeholder="Verified Full Name"
                        className={inputClasses}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(clubType === 'Alpha' || !clubType) && (
                        <div>
                            <label className={labelClasses}>Admission ID {clubType === 'Alpha' && '*'}</label>
                            <input
                                type="text"
                                value={formData.admission_number || ''}
                                onChange={(e) => setFormData({ ...formData, admission_number: e.target.value })}
                                required={clubType === 'Alpha'}
                                className={inputClasses}
                            />
                        </div>
                    )}
                    {(clubType === 'Omega' || !clubType) && (
                        <div>
                            <label className={labelClasses}>National Identity {clubType === 'Omega' && '*'}</label>
                            <input
                                type="text"
                                value={formData.nic_number || ''}
                                onChange={(e) => setFormData({ ...formData, nic_number: e.target.value })}
                                required={clubType === 'Omega'}
                                className={inputClasses}
                            />
                        </div>
                    )}
                </div>

                <div className="flex gap-4 pt-6">
                    <button type="button" onClick={closeModal} className="flex-1 py-4 text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:text-slate-900 transition-colors">Abort</button>
                    <button type="submit" disabled={loading} className="flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                        {loading ? 'Submitting...' : 'Mark Presence'}
                    </button>
                </div>
            </form>
        );
    }

    // Create/Edit Project Form
    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                    <label className={labelClasses}>Project Technical Name</label>
                    <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="e.g. Annual Blood Donation 2024"
                        className={inputClasses}
                    />
                </div>

                <div className="md:col-span-2">
                    <label className={labelClasses}>Deployment Category</label>
                    <select
                        value={formData.category_id || ''}
                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        required
                        className={inputClasses}
                    >
                        <option value="">Select Priority Category</option>
                        {projectCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                </div>

                <div className="md:col-span-2">
                    <label className={labelClasses}>Project Mission Brief</label>
                    <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className={`${inputClasses} resize-none`}
                        rows="4"
                        placeholder="Brief overview of project objectives..."
                    ></textarea>
                </div>

                <div>
                    <label className={labelClasses}>Operational Start</label>
                    <input
                        type="datetime-local"
                        value={formData.start_time ? formData.start_time.replace(' ', 'T') : ''}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        className={inputClasses}
                    />
                </div>
                <div>
                    <label className={labelClasses}>Operational End</label>
                    <input
                        type="datetime-local"
                        value={formData.end_time ? formData.end_time.replace(' ', 'T') : ''}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        className={inputClasses}
                    />
                </div>
            </div>

            <div className="p-6 bg-slate-50/50 rounded-3xl border border-slate-100 space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
                    <span className="text-[10px] font-black tracking-[0.2em] text-slate-400 uppercase flex items-center gap-2">
                        <FaMapMarkerAlt className="text-indigo-500" /> Geographic Anchoring
                    </span>
                    <button
                        type="button"
                        onClick={fetchGpsLocation}
                        disabled={geoLoading || linkLoading}
                        className="text-[9px] font-bold uppercase tracking-widest bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1"
                    >
                        <FaCrosshairs /> {geoLoading ? 'Scanning...' : 'Fetch Device GPS'}
                    </button>
                </div>
                
                {geoError && <div className="text-[10px] text-red-500 font-bold mb-2">{geoError}</div>}

                <div className="flex gap-2">
                    <input 
                        type="url" 
                        value={mapLink} 
                        onChange={(e) => setMapLink(e.target.value)} 
                        placeholder="Paste a Google Maps URL... (e.g. maps.app.goo.gl/...)" 
                        className="flex-1 px-4 py-3 rounded-2xl border border-slate-100 bg-white text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500/20 outline-none placeholder:font-normal placeholder:text-slate-400"
                    />
                    <button
                        type="button"
                        onClick={handleResolveLink}
                        disabled={!mapLink || linkLoading || geoLoading}
                        className="px-5 py-3 bg-slate-900 text-white rounded-2xl text-[10px] uppercase tracking-widest font-black whitespace-nowrap disabled:opacity-50 hover:bg-black transition-colors"
                    >
                        {linkLoading ? 'Parsing...' : 'Extract'}
                    </button>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest pl-1 mb-1 block">Latitude</label>
                        <input
                            type="number"
                            step="any"
                            value={formData.latitude || ''}
                            onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) })}
                            className="w-full px-4 py-3 rounded-2xl border border-slate-100 bg-white text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                            placeholder="40.7128"
                        />
                    </div>
                    <div>
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest pl-1 mb-1 block">Longitude</label>
                        <input
                            type="number"
                            step="any"
                            value={formData.longitude || ''}
                            onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) })}
                            className="w-full px-4 py-3 rounded-2xl border border-slate-100 bg-white text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500/20 outline-none"
                            placeholder="-74.0060"
                        />
                    </div>
                </div>
            </div>

            <div className="p-6 bg-slate-50/50 rounded-3xl border border-slate-100 flex items-center justify-between">
                <div>
                    <label className="text-[10px] font-black text-slate-900 uppercase tracking-widest block mb-0.5">Project Status</label>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Synchronize and toggle public access</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer group">
                    <input
                        type="checkbox"
                        id="is_open"
                        className="sr-only peer"
                        checked={Number(formData.is_open) === 1}
                        onChange={(e) => setFormData({ ...formData, is_open: e.target.checked ? 1 : 0 })}
                    />
                    <div className="w-14 h-8 bg-slate-200 peer-focus:outline-none rounded-2xl peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-slate-300 after:border after:rounded-xl after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500 shadow-inner"></div>
                </label>
            </div>

            <div className="flex gap-4 pt-6">
                <button type="button" onClick={closeModal} className="flex-1 py-4 text-slate-400 font-bold text-[10px] uppercase tracking-widest hover:text-slate-900 transition-colors">Discard</button>
                <button type="submit" disabled={loading} className="flex-[2] py-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-600/20 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {loading ? 'Saving State...' : (formData.id ? 'Commit Update' : 'Initialize Project')}
                </button>
            </div>
        </form>
    );
};

export default ClubModals;
